import LegalPageLayout from "@/components/LegalPageLayout";

/**
 * Public Terms of Service page (/terms).
 *
 * Substantive SaaS terms tailored to MatchBroker's B2B PH real estate
 * broker network. Update the effective date whenever terms change.
 */
export default function TermsOfServicePage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      effectiveDate="September 4, 2026"
      testId="terms-page"
    >
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to
        and use of MatchBroker (the &ldquo;Service&rdquo;), a professional
        network and matching platform for licensed Philippine real estate
        brokers, operated by <strong>Vancor Distribution, OPC</strong>{" "}
        (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). By creating
        an account or otherwise using the Service, you agree to these Terms
        and to our{" "}
        <a href="/privacy">Privacy Policy</a>. If you do not agree, do not
        use the Service.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        The Service is offered exclusively to individuals who (a) are 18
        years of age or older, and (b) hold a valid, current Philippine
        Regulation Commission (PRC) real estate broker license. You are
        responsible for providing accurate PRC license information at
        registration and for keeping it up to date. Any misrepresentation
        of your license status is grounds for immediate termination and
        removal from the network without refund.
      </p>

      <h2>2. Account &amp; verification</h2>
      <ul>
        <li>
          <strong>Application &amp; review</strong>: admission to the network
          is subject to verification by our team, which may involve
          checking your PRC ID image against the PRC public register. We
          may accept, reject, or later revoke access at our discretion,
          consistent with these Terms.
        </li>
        <li>
          <strong>One account per broker</strong>: you may not create
          multiple accounts. If you have lost access, use the password
          reset flow at{" "}
          <a href="/forgot-password">/forgot-password</a> or contact us.
        </li>
        <li>
          <strong>Credential security</strong>: you are responsible for
          maintaining the confidentiality of your password and any session
          tokens. Notify us promptly at{" "}
          <a href="mailto:info@matchbroker.ph">info@matchbroker.ph</a> if
          you suspect unauthorized access.
        </li>
        <li>
          <strong>Accurate information</strong>: all information you provide
          about yourself, your listings, and your buyer requirements must be
          true and complete to the best of your knowledge.
        </li>
      </ul>

      <h2>3. Content standards &amp; moderation</h2>
      <ul>
        <li>
          <strong>Authorized listings only</strong>: you may only post
          properties you are lawfully authorized to broker (whether via
          Authority to Sell, principal ownership, or an authorized
          co-broker chain).
        </li>
        <li>
          <strong>No fake or duplicate listings</strong>: we run automated
          duplicate detection (&ldquo;footprint&rdquo; hashing across
          building/project, unit identifier, and category) and reject
          duplicate submissions with a{" "}
          <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs">
            409 DUPLICATE_LISTING
          </code>{" "}
          error.
        </li>
        <li>
          <strong>Exclusive ATS claims must be genuine</strong>: marking a
          listing as an exclusive Authority to Sell blocks other brokers
          from listing the same footprint. If another broker holds a
          genuine competing ATS, our dispute-resolution process applies,
          and any decision we make (including unpublishing the disputed
          listing) is final at our sole discretion, subject to good-faith
          review of the documentary evidence submitted by both parties.
        </li>
        <li>
          <strong>Report and moderation rights</strong>: brokers may flag
          listings they believe violate these standards. Our moderation
          team may unpublish, edit, or remove any content we determine to
          violate these Terms or applicable law, without prior notice.
        </li>
      </ul>

      <h2>4. Subscriptions, trial, and billing</h2>
      <ul>
        <li>
          <strong>Free trial</strong>: new verified brokers receive a
          <strong> 30-day trial or 3 matches</strong>, whichever comes
          first. Certain protected actions (matching, pipeline status
          changes) return a{" "}
          <code className="mx-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs">
            402 UPGRADE_REQUIRED
          </code>{" "}
          error once the trial is exhausted.
        </li>
        <li>
          <strong>Pricing (as of the effective date above)</strong>:
          Monthly Pro <strong>₱499/month</strong>; 6-month Pro{" "}
          <strong>₱2,394</strong> (equivalent to ₱399/month); Annual Pro{" "}
          <strong>₱3,588</strong> (equivalent to ₱299/month). All prices
          are inclusive of applicable Philippine taxes. Prices may change
          on 30 days&rsquo; notice, applied to renewals only.
        </li>
        <li>
          <strong>Payment processor</strong>: subscriptions are billed
          through PayMongo (Philippine payments processor). We do not
          store card, GCash, or Maya credentials.
        </li>
        <li>
          <strong>No refunds for partial periods</strong>: except where
          required by Philippine consumer protection law, we do not refund
          unused portions of a billing cycle.
        </li>
        <li>
          <strong>Cancellation &amp; renewal</strong>: cancelling stops the
          next renewal charge; your subscription remains active through
          the end of the current paid period. We do not charge auto-renewal
          fees without your prior consent.
        </li>
        <li>
          <strong>Administrator-granted credits</strong>: our support team
          may occasionally grant Pro time, trial extensions, or referral
          credits at their discretion. Such grants are not entitlements
          and may be revoked if the grant was based on false or misleading
          information.
        </li>
      </ul>

      <h2>5. Platform role &amp; disclaimers</h2>
      <p>
        MatchBroker is a networking and matching <em>tool</em>. It is{" "}
        <strong>not</strong> a party to any real estate transaction,
        listing agreement, buyer&rsquo;s agreement, or commission split
        between users. We do not verify the accuracy of any listing or
        requirement beyond broker license verification, and we make no
        representation or warranty that:
      </p>
      <ul>
        <li>you will find matches, close deals, or earn commissions;</li>
        <li>
          any AI-derived output (extracted fields, match scores,
          suggestions) is accurate, complete, or free of error — outputs
          are assistive and you remain responsible for reviewing them
          before acting;
        </li>
        <li>
          any content posted by other brokers is accurate, current, or
          non-infringing;
        </li>
        <li>the Service will be uninterrupted or error-free.</li>
      </ul>
      <p>
        Except as required by Philippine law, the Service is provided
        &ldquo;AS IS&rdquo; without warranty of any kind.
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          use the Service to violate any applicable law or PRC regulation;
        </li>
        <li>
          scrape, mass-download, or reverse-engineer any portion of the
          Service or its data;
        </li>
        <li>
          interfere with security features, rate limits, or authentication
          gates;
        </li>
        <li>
          impersonate another broker, share credentials, or misrepresent
          your professional standing;
        </li>
        <li>
          upload content that is unlawful, infringing, defamatory,
          harassing, or discriminatory.
        </li>
      </ul>

      <h2>7. Intellectual property</h2>
      <p>
        The Service, including the MatchBroker name, logo, source code,
        user interface, and AI models, is owned by or licensed to Vancor
        Distribution, OPC and protected by intellectual property laws. You
        retain ownership of the listing and requirement content you post,
        and you grant us a worldwide, royalty-free, non-exclusive licence
        to store, process, transmit, and display that content solely as
        needed to operate the Service and deliver features you use
        (matching, notifications, co-broker sharing).
      </p>

      <h2>8. Termination</h2>
      <p>
        You may close your account at any time by contacting us. We may
        suspend or terminate your account without notice if you (a)
        materially breach these Terms, (b) provide false verification
        data, (c) engage in fraudulent listings or exclusive-ATS
        misrepresentation, or (d) create risk or liability for us or
        other users. Upon termination, your access ends and your data is
        retained or deleted per our{" "}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>9. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by Philippine law, in no event
        will Vancor Distribution, OPC or its affiliates be liable for any
        indirect, incidental, special, consequential, or punitive damages,
        or any loss of profits, revenue, goodwill, or data. Our aggregate
        liability for any claim arising out of or relating to these Terms
        or the Service will not exceed the fees you paid to us for the
        Service in the twelve (12) months preceding the event giving rise
        to the claim, or ₱5,000, whichever is greater.
      </p>

      <h2>10. Indemnity</h2>
      <p>
        You agree to indemnify and hold harmless Vancor Distribution, OPC
        and its officers, employees, and agents from any claim, loss,
        damage, or expense (including reasonable legal fees) arising out
        of your breach of these Terms, your content, or your use of the
        Service.
      </p>

      <h2>11. Governing law &amp; disputes</h2>
      <p>
        These Terms are governed by the laws of the Republic of the
        Philippines, without regard to conflict-of-law principles. Any
        dispute arising out of or relating to these Terms or the Service
        will be brought exclusively before the competent courts of Metro
        Manila, Philippines, except that either party may seek injunctive
        relief in any court of competent jurisdiction to protect its
        intellectual property or confidential information.
      </p>

      <h2>12. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes
        will be signalled via an in-app banner or email to your registered
        address at least seven (7) days before they take effect. Continued
        use of MatchBroker after an update constitutes acceptance of the
        revised Terms.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms?{" "}
        <a href="mailto:info@matchbroker.ph">info@matchbroker.ph</a>.
        Postal address available on request.
      </p>
    </LegalPageLayout>
  );
}
